import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Mic, MicOff, Send, Pencil, Clock, X, Volume2, Square } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { generalActions } from "../../store/actions";
import agentService from "../../store/services/agent.service";
import hanshiLogo from "../../assets/hanshi/full-sm.svg";
import styles from "./VoiceAgentPanel.module.scss";
import "./VoiceAgentShell.scss";

const CHART_COLORS = ["#342c82", "#5f4efc", "#7736d1", "#a56af5", "#c5abfd"];
const CHART_TYPES = ["bar_chart", "line_chart", "pie_chart"];

// Retry waits can range from a ~30s provider rate limit up to a full 24h token-quota window, so
// a raw second count is only readable at the short end - drop to whichever unit is coarsest
// while still being meaningful (hours+minutes, then minutes, then seconds).
function formatRetryDuration(totalSeconds) {
  if (totalSeconds <= 0) return "";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

const ISO_UTC_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

// Query results are JSON, so a UTC datetime column round-trips as a raw ISO string like
// "2026-09-08T18:25:25.04Z" - meaningless shown as-is, so convert to the viewer's own browser
// timezone. A plain date (no time component, no trailing Z) is left alone - a calendar day is
// the same everywhere, there's nothing to convert.
function formatCellValue(value) {
  if (value == null) return "";
  if (typeof value === "string" && ISO_UTC_DATETIME_RE.test(value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime()))
      return parsed.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  }
  return String(value);
}

function DataTable({ rows }) {
  if (!rows?.length) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>{columns.map((c) => <td key={c}>{formatCellValue(row[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultChart({ displayType, rows, chart }) {
  const { xField, yField } = chart;

  if (displayType === "bar_chart") {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xField} tickFormatter={formatCellValue} />
          <YAxis />
          <Tooltip labelFormatter={formatCellValue} />
          <Bar dataKey={yField} fill={CHART_COLORS[0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (displayType === "line_chart") {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xField} tickFormatter={formatCellValue} />
          <YAxis />
          <Tooltip labelFormatter={formatCellValue} />
          <Line type="monotone" dataKey={yField} stroke={CHART_COLORS[0]} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie data={rows} dataKey={yField} nameKey={xField} label>
          {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

function ResponsePayload({ response }) {
  const { displayType, rows, chart } = response;

  if (displayType === "table")
    return <DataTable rows={rows} />;

  if (CHART_TYPES.includes(displayType)) {
    const hasFields = rows?.length && chart?.xField in rows[0] && chart?.yField in rows[0];
    if (hasFields)
      return <ResultChart displayType={displayType} rows={rows} chart={chart} />;
    return <DataTable rows={rows} />;
  }

  return null;
}

// Reveals `text` progressively over a fixed number of ticks (not fixed time-per-char), so a
// long answer doesn't take forever to finish appearing - gives the ChatGPT-style "typing" feel
// even though the backend returns the full answer in one shot rather than streaming tokens.
function TypewriterText({ text }) {
  const [shown, setShown] = useState("");

  useEffect(() => {
    if (!text) {
      setShown("");
      return undefined;
    }
    setShown("");
    let i = 0;
    const step = Math.max(1, Math.ceil(text.length / 50));
    const interval = setInterval(() => {
      i += step;
      setShown(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, 18);
    return () => clearInterval(interval);
  }, [text]);

  return <>{shown}</>;
}

function ThinkingIndicator() {
  return (
    <div className={styles.thinkingIndicator}>
      <div className={styles.thinkingBubble}>
        <span className={styles.thinkingDot} />
        <span className={styles.thinkingDot} />
        <span className={styles.thinkingDot} />
      </div>
    </div>
  );
}

function MessageBubble({ message, onEdit, onReadAloud, onStopReading, isSpeaking }) {
  if (message.role === "user")
    return (
      <div className={styles.userBubbleRow}>
        <button
          type="button"
          className={styles.editButton}
          onClick={() => onEdit(message.text)}
          title="Edit and ask again"
        >
          <Pencil size={12} />
        </button>
        <div className={styles.userBubble}>{message.text}</div>
      </div>
    );

  if (message.status === "thinking")
    return <ThinkingIndicator />;

  if (message.status === "error")
    return <div className={styles.errorBanner}>{message.error}</div>;

  return (
    <div className={styles.assistantArea}>
      <div className={styles.assistantBubbleRow}>
        <div className={styles.assistantBubble}>
          <TypewriterText text={message.response.spokenText} />
        </div>
        {isSpeaking ? (
          <button
            type="button"
            className={`${styles.speakButton} ${styles.speakButtonActive}`}
            onClick={onStopReading}
            title="Stop reading"
          >
            <Square size={11} fill="currentColor" />
          </button>
        ) : (
          <button
            type="button"
            className={styles.speakButton}
            onClick={() => onReadAloud(message.response.spokenText, message.id)}
            title="Read aloud"
          >
            <Volume2 size={13} />
          </button>
        )}
      </div>
      <ResponsePayload response={message.response} />
    </div>
  );
}

const VoiceAgentPanel = ({ status, onStatusChange, onClose }) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [micDenied, setMicDenied] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState(null);
  const [rateLimitSecondsLeft, setRateLimitSecondsLeft] = useState(0);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);

  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const submitQuestionRef = useRef(() => {});
  const finishListeningRef = useRef(() => {});
  const idCounterRef = useRef(0);
  const conversationRef = useRef(null);
  const textInputRef = useRef(null);
  const cancelledRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const micStreamRef = useRef(null);
  const speechTokenRef = useRef(0);

  const isRateLimited = rateLimitSecondsLeft > 0;

  const SpeechRecognitionCtor =
    typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechSupported = !!SpeechRecognitionCtor;

  const nextId = () => `m${++idCounterRef.current}`;

  useEffect(() => {
    conversationRef.current?.scrollTo({ top: conversationRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Ticks the rate-limit countdown down to zero and automatically re-enables asking - no manual
  // retry or page refresh needed once the expiry passes.
  useEffect(() => {
    if (!rateLimitedUntil) return undefined;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000));
      setRateLimitSecondsLeft(remaining);
      if (remaining <= 0) {
        setRateLimitedUntil(null);
        onStatusChange("idle");
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [rateLimitedUntil, onStatusChange]);

  const speak = useCallback((text, messageId = null) => {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) {
      onStatusChange("idle");
      setSpeakingMessageId(null);
      return;
    }
    window.speechSynthesis.cancel();
    // Guards against a stale onend/onerror from a just-cancelled utterance clearing the state
    // set by the new one it was cancelled in favor of.
    const token = ++speechTokenRef.current;
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.onend = () => {
      if (speechTokenRef.current !== token) return;
      onStatusChange("idle");
      setSpeakingMessageId(null);
    };
    utterance.onerror = () => {
      if (speechTokenRef.current !== token) return;
      onStatusChange("idle");
      setSpeakingMessageId(null);
    };
    setSpeakingMessageId(messageId);
    window.speechSynthesis.speak(utterance);
  }, [onStatusChange]);

  // Manual replay via the per-message speaker icon - always reads aloud regardless of how the
  // question was originally asked.
  const readAloud = useCallback((text, messageId) => {
    onStatusChange("speaking");
    speak(text, messageId);
  }, [onStatusChange, speak]);

  const stopSpeaking = useCallback(() => {
    speechTokenRef.current++;
    setSpeakingMessageId(null);
    onStatusChange("idle");
    if (typeof window !== "undefined" && window.speechSynthesis)
      window.speechSynthesis.cancel();
  }, [onStatusChange]);

  const submitQuestion = useCallback(async (question, { viaVoice = false } = {}) => {
    const trimmed = question?.trim();
    if (!trimmed) return;

    const userMsgId = nextId();
    const assistantMsgId = nextId();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: "user", text: trimmed },
      { id: assistantMsgId, role: "assistant", status: "thinking" },
    ]);
    onStatusChange("thinking");

    try {
      const result = await agentService.askQuestion(trimmed);
      setMessages((prev) => prev.map((m) =>
        m.id === assistantMsgId ? { ...m, status: "done", response: result } : m
      ));

      if (result.retryAfterSeconds) {
        setRateLimitedUntil(Date.now() + result.retryAfterSeconds * 1000);
        onStatusChange("error");
        return;
      }

      // Only auto-read the response aloud when the question itself was asked by voice -
      // a typed question stays silent unless the user clicks the speaker icon.
      if (viaVoice) {
        onStatusChange("speaking");
        speak(result.spokenText, assistantMsgId);
      } else {
        onStatusChange("idle");
      }
    } catch (err) {
      const message = err?.data?.message || err?.message || "Something went wrong. Please try again.";
      setMessages((prev) => prev.map((m) =>
        m.id === assistantMsgId ? { ...m, status: "error", error: message } : m
      ));
      onStatusChange("error");
      dispatch(generalActions.showToast({ title: message, type: "error" }));
    }
  }, [onStatusChange, speak, dispatch]);

  useEffect(() => {
    submitQuestionRef.current = submitQuestion;
  }, [submitQuestion]);

  // Best-effort mic capture for Whisper, started alongside SpeechRecognition - if getUserMedia or
  // MediaRecorder is unavailable/denied, this silently no-ops and the SpeechRecognition-only flow
  // continues exactly as before Whisper existed.
  const startMediaRecording = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined")
      return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const recorder = new window.MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      micStreamRef.current = stream;
    } catch {
      mediaRecorderRef.current = null;
      micStreamRef.current = null;
    }
  }, []);

  // Discards any in-progress recording and releases the mic immediately - used when cancelling
  // or unmounting, where the audio is never going to be uploaded.
  const releaseMediaRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch {
        // already stopped - ignore
      }
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
  }, []);

  // Stops the recorder and resolves with its final audio Blob (or null if there was no active
  // recording), releasing the mic stream once the recorder has actually finished.
  const stopMediaRecordingAndGetBlob = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    const stream = micStreamRef.current;
    mediaRecorderRef.current = null;

    if (!recorder) {
      stream?.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const finish = (blob) => {
        stream?.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
        resolve(blob);
      };
      recorder.onstop = () => {
        const blob = audioChunksRef.current.length
          ? new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" })
          : null;
        audioChunksRef.current = [];
        finish(blob);
      };
      try {
        if (recorder.state !== "inactive") recorder.stop();
        else finish(null);
      } catch {
        finish(null);
      }
    });
  }, []);

  // Replaces the decision logic that used to live inline in recognition.onend. Tries Whisper
  // first (more accurate on domain vocabulary); on any failure - timeout, network error, or empty
  // result - silently falls back to the SpeechRecognition transcript with no visible error, per
  // the requirement that a Whisper failure never blocks or surfaces to the user.
  const finishListening = useCallback(async () => {
    const speechTranscript = finalTranscriptRef.current?.trim();
    finalTranscriptRef.current = "";

    if (cancelledRef.current) {
      cancelledRef.current = false;
      releaseMediaRecording();
      return;
    }

    const audioBlob = await stopMediaRecordingAndGetBlob();
    let finalQuestion = speechTranscript;

    if (audioBlob && !cancelledRef.current) {
      try {
        const transcription = await agentService.transcribeAudio(audioBlob, { timeoutMs: 6000 });
        const whisperText = transcription?.text?.trim();
        if (whisperText) finalQuestion = whisperText;
      } catch {
        // Whisper failed - fall back to the SpeechRecognition transcript below.
      }
    }

    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }

    if (finalQuestion) {
      setInputValue("");
      submitQuestionRef.current(finalQuestion, { viaVoice: true });
    } else {
      onStatusChange("idle");
    }
  }, [onStatusChange, releaseMediaRecording, stopMediaRecordingAndGetBlob]);

  useEffect(() => {
    finishListeningRef.current = finishListening;
  }, [finishListening]);

  useEffect(() => {
    if (!speechSupported) return undefined;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => onStatusChange("listening");

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = 0; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += text;
        else interimText += text;
      }
      finalTranscriptRef.current = finalText;
      setInputValue(finalText || interimText);
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        setMicDenied(true);
        dispatch(generalActions.showToast({ title: "Microphone access was denied - you can still type your question.", type: "error" }));
      }
      if (event.error !== "no-speech" && event.error !== "aborted")
        onStatusChange("idle");
    };

    recognition.onend = () => {
      finishListeningRef.current();
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.stop();
      if (typeof window !== "undefined" && window.speechSynthesis)
        window.speechSynthesis.cancel();
      releaseMediaRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicClick = useCallback(() => {
    if (!speechSupported || !recognitionRef.current) return;
    if (status === "listening") {
      recognitionRef.current.stop();
      return;
    }
    setInputValue("");
    try {
      recognitionRef.current.start();
      startMediaRecording();
    } catch {
      // already listening - ignore
    }
  }, [speechSupported, status, startMediaRecording]);

  const handleCancelListening = useCallback(() => {
    if (!recognitionRef.current) return;
    cancelledRef.current = true;
    finalTranscriptRef.current = "";
    releaseMediaRecording();
    recognitionRef.current.abort();
    setInputValue("");
    onStatusChange("idle");
  }, [onStatusChange, releaseMediaRecording]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const question = inputValue.trim();
    if (!question || status === "thinking" || isRateLimited) return;
    if (status === "listening") recognitionRef.current?.stop();
    setInputValue("");
    submitQuestion(question);
  };

  const handleEditMessage = useCallback((text) => {
    setInputValue(text);
    textInputRef.current?.focus();
  }, []);

  const inputDisabled = status === "thinking" || isRateLimited;

  return (
    <div className={styles.panel}>
      <div className={styles.conversationArea} ref={conversationRef}>
        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <img src={hanshiLogo} alt="Hanshi AI" className={styles.emptyLogo} />
            <p>Ask Hanshi AI about your portfolios, programs, projects, budgets, risks, or health status.</p>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            onEdit={handleEditMessage}
            onReadAloud={readAloud}
            onStopReading={stopSpeaking}
            isSpeaking={speakingMessageId === m.id}
          />
        ))}
      </div>

      {isRateLimited && (
        <div className={styles.rateLimitNotice}>
          <Clock size={13} />
          <span>You’ve reached the usage limit for Hanshi AI - you can ask again in {formatRetryDuration(rateLimitSecondsLeft)}</span>
        </div>
      )}

      <form className={styles.inputBar} onSubmit={handleSubmit}>
        {speechSupported && status === "listening" && (
          <button
            type="button"
            className={styles.cancelListeningButton}
            onClick={handleCancelListening}
            title="Cancel recording"
          >
            <X size={16} />
          </button>
        )}
        {speechSupported && (
          <button
            type="button"
            className={`${styles.micIconButton} ${status === "listening" ? styles.micActive : ""}`}
            onClick={handleMicClick}
            disabled={inputDisabled || micDenied}
            title={micDenied ? "Microphone access denied" : status === "listening" ? "Stop and send" : "Ask by voice"}
          >
            {status === "listening" ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
        )}
        <input
          ref={textInputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Message Hanshi AI…"
          className={styles.textInput}
          disabled={inputDisabled}
        />
        <button
          type="submit"
          className={styles.sendButton}
          disabled={!inputValue.trim() || inputDisabled}
          title="Send"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

export default VoiceAgentPanel;
